import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Mutation, Query, Subscription } from '@adornis/baseql/decorators.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { DynamicClass } from '@adornis/cms/db/dynamic-classes.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { catchError, from, of, switchMap } from 'rxjs';
import { KPF } from '../../../master-data-management/kpf/entities/KPF.js';
import { ChancenDynamicClassProductFilter } from './ChancenDynamicClassProductFilter.js';
import { Product } from './Product.js';

@Entity()
export class ChancenDynamicClassProduct extends DynamicClass {
  public static override _class = 'ChancenDynamicClassProduct';
  public static override _collectionName = 'dynamic-class-products';

  @Field(() => String)
  public override collectionName: string = Product._collectionName;

  @Field(() => String)
  public override parentClassName: string = Product._class;

  @validate(nonOptional())
  @Field(() => String)
  public name!: string;

  @validate(nonOptional())
  @Field(() => String)
  public kuerzel!: string;

  // @Field(() => [CapacityPerRole], { default: v => v ?? [] })
  // public capacityPerRole: Maybe<CapacityPerRole[]>;

  // @Field(() => [CapacityPerCourse], { default: v => v ?? [] })
  // public capacityPerCourse: Maybe<CapacityPerCourse[]>;

  @Mutation(() => Boolean)
  static checkDuplicateKuerzelForDynamicProduct(
    @Arg('kuerzel', () => String) kuerzel: string,
    @Arg('id', () => String) id: string,
  ) {
    return async (gqlFields: void) => {
      const dynamicProductCollection = await getRawCollection<ChancenDynamicClassProduct>(this._collectionName);
      const duplicates = await dynamicProductCollection
        .find({ $and: [{ $or: [{ class: Product._class + kuerzel }, { kuerzel }] }, { _id: { $ne: id } }] })
        .toArray();
      return duplicates && duplicates.length;
    };
  }

  @Subscription(() => [ChancenDynamicClassProduct])
  static subscribeProductsByFilterPaginated(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('filter', () => ChancenDynamicClassProductFilter) filter: ChancenDynamicClassProductFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<ChancenDynamicClassProduct>) => {
      const selector = filter.getMongoFilter();
      const handle = getCollectionHandle<ChancenDynamicClassProduct>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            skip,
            limit,
            sort: { kuerzel: -1 },
          });
        }),
        catchError(err => {
          logger.error({ err }, 'Error during subscription');
          return of([] as ChancenDynamicClassProduct[]);
        }),
      );
    };
  }

  @Query(() => ChancenDynamicClassProduct)
  static getChancenDynamicClassProductByKuerzel(@Arg('kuerzel', () => String) kuerzel: string) {
    return async (gqlFields: BaseQLSelectionSet<ChancenDynamicClassProduct>) => {
      const dynamicProductCollection = await getRawCollection<ChancenDynamicClassProduct>(this._collectionName);
      const result = await dynamicProductCollection.findOne({ kuerzel });

      return result;
    };
  }

  @Query(() => ChancenDynamicClassProduct)
  static getChancenDynamicClassProductByClassName(@Arg('className', () => String) className: string) {
    return async (gqlFields: BaseQLSelectionSet<ChancenDynamicClassProduct>) => {
      const dynamicProductCollection = await getRawCollection<ChancenDynamicClassProduct>(this._collectionName);
      const result = await dynamicProductCollection.findOne({ class: className });

      return result;
    };
  }

  override toString() {
    return this.kuerzel + ' ' + this.name;
  }

  public static sanatizeKuerzel(kuerzel: string) {
    return kuerzel.toUpperCase().replace(/ /, '');
  }

  public static generateClassNameFromKuerzel(kuerzel: string) {
    return (
      Product._class + kuerzel.replace(/ /, '').replace(/-/, '').replace(/_/, '').replace(/\(/, '').replace(/\)/, '')
    );
  }

  public generateClass(): Maybe<Product> {
    try {
      const nodeFromDynamicProduct = baseqlMetaData.entities.resolveNode(this.class);
      const ClassFromDynamicProduct = nodeFromDynamicProduct.type() as typeof Product;
      return new ClassFromDynamicProduct({
        name: this.name,
        kuerzel: this.kuerzel,
      });
    } catch (error) {
      console.error(error);
    }
    return null;
  }

  static override async beforeRemove(id: string, entityData: ChancenDynamicClassProduct): Promise<void> {
    const kpfCollection = await getRawCollection(KPF._collectionName);
    const kpfsWithProduct = await kpfCollection
      .find({ 'produkt._class': 'Product' + entityData.kuerzel }, { projection: { _id: 1 } })
      .toArray();
    if (kpfsWithProduct.length) {
      throw new Error(
        'Produkt kann nicht gelöscht werden! Es gibt noch ' + kpfsWithProduct.length + ' KPFs mit diesem Produkt.',
      );
    }

    return super.beforeRemove(id, entityData);
  }
}

if (A.isServer) {
  const products = getRawCollection<any>(ChancenDynamicClassProduct._collectionName);

  // Create DB Indices
  void products.then(async db => {
    await db.createIndex({ class: 1 });
    await db.createIndex({ kuerzel: 1 });
  });
}
